const DEFAULT_MAX_CAPTION_CHARS = 24;
const CAPTION_LEAD_SECONDS = 0.28;
const DEFAULT_CAPTION_CHARS_PER_SECOND = 4;

export interface CaptionTimeline {
  startTimes: number[];
}

interface SegmentData {
  segment: string;
}

interface WordSegmenter {
  segment(input: string): Iterable<SegmentData>;
}

type SegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity?: "word" },
) => WordSegmenter;

function segmentWords(text: string): string[] {
  const Segmenter = (
    Intl as typeof Intl & { Segmenter?: SegmenterConstructor }
  ).Segmenter;
  if (Segmenter) {
    return Array.from(
      new Segmenter("ja", { granularity: "word" }).segment(text),
      (item) => item.segment,
    );
  }

  // 古いブラウザでは英数字と空白だけでも単語途中の分割を避ける。
  return text.match(/[A-Za-z0-9]+|\s+|[^A-Za-z0-9\s]+/gu) ?? [text];
}

function isNaturalBreakWord(word: string): boolean {
  return (
    /^[、。，,.！？!?；;：:]$/u.test(word) ||
    /(?:は|が|を|に|で|と|や|も|へ|から|まで|ので|ため|ながら|うえで)$/u.test(
      word,
    )
  );
}

function wordsLength(words: readonly string[]): number {
  return words.join("").length;
}

function preferredBreakIndex(words: readonly string[], maxChars: number): number {
  const minimumLength = Math.floor(maxChars * 0.5);
  let length = 0;
  let candidate = -1;
  words.forEach((word, index) => {
    length += word.length;
    if (length >= minimumLength && isNaturalBreakWord(word)) candidate = index;
  });
  return candidate;
}

/**
 * 読み上げ文を、音声と一緒に1行ずつ出せる短い発話へ分割する。
 * 句点を優先し、長い文は日本語の単語境界で分ける。
 * 24文字を超える単語は、単語自体を切らず1行のまま残す。
 */
export function splitSpeechCaptionLines(
  source: string,
  maxChars = DEFAULT_MAX_CAPTION_CHARS,
): string[] {
  const normalized = source.replace(/\s+/gu, " ").trim();
  if (!normalized) return [];

  const lines: string[] = [];
  let currentWords: string[] = [];
  for (const word of segmentWords(normalized)) {
    const isPunctuation = /^[、。，,.！？!?；;：:]+$/u.test(word);
    if (
      !isPunctuation &&
      currentWords.length > 0 &&
      wordsLength(currentWords) + word.length > maxChars
    ) {
      const breakIndex = preferredBreakIndex(currentWords, maxChars);
      if (breakIndex >= 0) {
        const completed = currentWords
          .slice(0, breakIndex + 1)
          .join("")
          .trim();
        if (completed) lines.push(completed);
        currentWords = currentWords.slice(breakIndex + 1);
      } else {
        const completed = currentWords.join("").trim();
        if (completed) lines.push(completed);
        currentWords = [];
      }
    }
    currentWords.push(word);
  }
  const remaining = currentWords.join("").trim();
  if (remaining) lines.push(remaining);

  return lines;
}

function speechWeight(text: string): number {
  const spokenChars = text.replace(/\s+/gu, "").length;
  const pauseWeight = (text.match(/[、。，,.！？!?]/gu) ?? []).length * 1.5;
  return Math.max(1, spokenChars + pauseWeight);
}

function spokenCharacterCount(text: string): number {
  return Math.max(1, text.replace(/\s+/gu, "").length);
}

/**
 * 1秒あたり約4文字を目安に、各テロップの表示開始時刻を作る。
 * 音声が速い場合は字幕の間隔だけを縮め、音声の再生速度は変えない。
 */
export function buildCaptionTimeline(
  lines: readonly string[],
  durationSeconds: number,
  charsPerSecond = DEFAULT_CAPTION_CHARS_PER_SECOND,
): CaptionTimeline {
  if (lines.length === 0) return { startTimes: [] };
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return { startTimes: lines.map(() => 0) };
  }

  const safeCharsPerSecond = Math.max(0.1, charsPerSecond);
  const requiredDisplayDuration = lines.reduce(
    (total, line) =>
      total + spokenCharacterCount(line) / safeCharsPerSecond,
    0,
  );
  const durationScale = Math.min(1, durationSeconds / requiredDisplayDuration);
  const weights = lines.map(speechWeight);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  const minimumDurations = lines.map(
    (line) => spokenCharacterCount(line) / safeCharsPerSecond,
  );
  const spareDuration = Math.max(
    0,
    durationSeconds - requiredDisplayDuration,
  );
  let elapsedDisplayTime = 0;
  const startTimes = weights.map((weight, index) => {
    const start = Math.max(
      0,
      elapsedDisplayTime - CAPTION_LEAD_SECONDS,
    );
    elapsedDisplayTime +=
      minimumDurations[index] * durationScale +
      (spareDuration * weight) / totalWeight;
    return start;
  });

  return { startTimes };
}

export function captionIndexAtTime(
  startTimes: readonly number[],
  currentTimeSeconds: number,
): number {
  if (startTimes.length === 0) return -1;
  let activeIndex = 0;
  for (let index = 1; index < startTimes.length; index += 1) {
    if (currentTimeSeconds < startTimes[index]) break;
    activeIndex = index;
  }
  return activeIndex;
}
